<template>
  <section class="infowindow">
    <pu-campsite-link
      :hierarchy-path="campsite.path"
      :slug="campsite.slug"
      exclude-type="fullMinusType"
      :target="$isDesktop ? '_blank' : undefined"
      :filters="{ facets: facets }"
    >
      <div v-if="hasCampsiteImage" class="cs-image">
        <client-only>
          <campsite-favourite-button
            :campsite-id="campsite.id?.toString()"
            :campsite-name="campsite.name"
            class="cs-favourite"
          />
        </client-only>
        <img
          v-if="hasCampsiteImage && campsite.photo"
          loading="lazy"
          :src="campsite.photo.url.large"
          width="80"
          height="80"
        />
      </div>
      <section class="card-info">
        <h4>{{ campsite.name }}</h4>
        <span v-if="campsite.hierarchy" class="location">{{
          campsite.hierarchy
        }}</span>
        <div class="card-meta">
          <pu-category-icons :categories="rootCategories" />
          <pu-rating-bubble
            :rating="campsite.rating10"
            :rate-count="campsite.rateCount"
          />
        </div>
      </section>
    </pu-campsite-link>
    <footer>
      <slot name="footer" />
    </footer>
  </section>
</template>

<script lang="ts">
import { getCampsiteCategoriesPresenter } from '~/apps/categories/presenter-json/CampsiteCategoriesUpdaterJson'
import { Component, Prop, Vue, toNative } from '~/utility/pu-class-decorator'
import { MapCampsite } from '../../domain/MapsCampsite'

@Component({
  setup() {
    const searchStore = useSearchStoreSetup(usePinia())
    return { searchStore }
  },
})
class CampsiteMapCard extends Vue {
  @Prop({ required: true })
  campsite: MapCampsite

  @Prop({ required: false, default: true })
  showPrice: boolean

  @Prop({ required: false })
  isCampsiteDetails: boolean

  searchStore: ReturnType<typeof useSearchStoreSetup>

  get hasCampsiteImage(): boolean {
    return !!this.campsite?.photo?.url?.large
  }

  get rootCategories() {
    return this.categories.filter((category) =>
      this.isRootCategory(category.slug),
    )
  }

  get categories() {
    return this.campsite?.categories
      ? this.campsite.categories.map((c) => ({
          ...c,
          isPositive: true,
        }))
      : []
  }

  get priceVisible() {
    return this.campsite?.bookable && this.showPrice
  }

  get facets() {
    return this.searchStore.context.results?.pitchtypeFacets || []
  }

  private isRootCategory(categorySlug: string) {
    const categoriesPresenter = getCampsiteCategoriesPresenter(
      this.$route.params.lang || 'en-gb',
    )
    return !!categoriesPresenter.bySlugMap[categorySlug]
  }
}

export default toNative(CampsiteMapCard)
</script>

<style scoped>
a {
  text-decoration: none;
  display: flex;
  flex-direction: row;
  align-items: stretch;
  justify-content: flex-start;
  min-height: 5em;
}

.cs-image {
  position: relative;
}

.cs-image img {
  width: 8em;
  height: 100%;
  object-position: center;
  object-fit: cover;
}

.cs-image .cs-favourite {
  position: absolute;
  top: 0.75em;
  left: 0.75em;
}

.cs-image .cs-favourite :deep(svg) {
  width: 1.8em;
}

.cs-image
  .cs-favourite
  :deep(.favourite-add-form-modal .modal-trigger-wrapper) {
  padding: 0;
  background: transparent;
  border-radius: 0;
}

.card-info {
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  width: 100%;
  padding: 0.5em 0.75em;
}

h4 {
  font-size: 1.1em !important;
  line-height: 1.1;
  margin-bottom: 0;
  color: var(--colour-text-default);
}

.location {
  color: var(--colour-text-light);
  font-size: var(--body);
  display: block;
  margin: 0.25em 0;
}

.card-meta {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-bottom: 0.3em;
}

.card-meta :deep(.category-icons) {
  justify-content: flex-start;
}

.card-meta :deep(.rating-bubble) {
  top: -0.4em;
}

.card-meta :deep(.rating-bubble:before) {
  font-size: 10px;
  bottom: -1.2em;
}

footer :deep(.map-action) {
  border-top: 1px solid var(--colour-table-borders);
}
</style>
